import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { pastaData, calzoneData, sectionImages } from '../data/menuData';
import { Flame, Leaf, Star, Sparkles } from 'lucide-react';

const PASTA_BG = sectionImages.pasta;

const PastaCalzoneSection = () => {
  const { language, t } = useLanguage();
  const [activeTab, setActiveTab] = useState('pasta');

  const renderBadges = (item) => {
    const badges = [];
    if (item.isPopular) badges.push({ icon: <Star className="w-4 h-4 text-amber-400 fill-amber-400" />, key: 'popular' });
    if (item.isSpicy) badges.push({ icon: <Flame className="w-4 h-4 text-orange-500" />, key: 'spicy' });
    if (item.isVegetarian) badges.push({ icon: <Leaf className="w-4 h-4 text-emerald-500" />, key: 'vegetarian' });
    
    if (badges.length === 0) return null;
    return (
      <div className="flex items-center gap-1.5 ml-2">
        {badges.map((b) => <span key={b.key}>{b.icon}</span>)}
      </div>
    );
  };

  const currentItems = activeTab === 'pasta' ? pastaData : calzoneData;

  return (
    <section 
      id="pasta" 
      className="relative overflow-hidden"
      data-testid="pasta-section"
    >
      {/* Premium Section Header */}
      <div className="relative h-56 md:h-72 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${PASTA_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-hanse-dark/50 via-hanse-dark/60 to-hanse-dark/95" />
        
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 mb-4 bg-gradient-to-r from-red-500/20 to-red-600/10 backdrop-blur-sm px-4 py-2 rounded-full border border-red-400/30"
          >
            <Sparkles className="w-5 h-5 text-red-400" />
            <span className="text-red-300 font-semibold text-sm uppercase tracking-wider">
              {language === 'de' ? 'Italienische Klassiker' : 'Italian Classics'}
            </span>
            <Sparkles className="w-5 h-5 text-red-400" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3"
          >
            {t('menu.pasta.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg md:text-xl max-w-2xl"
          >
            {t('menu.pasta.subtitle')}
          </motion.p>
        </div>
      </div>

      {/* Premium Menu Content */}
      <div className="bg-gradient-to-b from-hanse-dark to-hanse-navy py-10 md:py-14">
        <div className="container mx-auto px-4 max-w-5xl">
          
          {/* Premium Tab Switcher */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mb-10"
          >
            <div className="inline-flex bg-white/5 rounded-full p-1.5 border border-white/10">
              <button
                onClick={() => setActiveTab('pasta')}
                className={`px-8 py-3 rounded-full font-bold text-sm transition-all duration-300 ${
                  activeTab === 'pasta'
                    ? 'bg-gradient-to-r from-hanse-red to-hanse-red/80 text-white shadow-lg'
                    : 'text-white/60 hover:text-white'
                }`}
                data-testid="pasta-tab"
              >
                Pasta ({pastaData.length})
              </button>
              <button
                onClick={() => setActiveTab('calzone')}
                className={`px-8 py-3 rounded-full font-bold text-sm transition-all duration-300 ${
                  activeTab === 'calzone'
                    ? 'bg-gradient-to-r from-hanse-red to-hanse-red/80 text-white shadow-lg'
                    : 'text-white/60 hover:text-white'
                }`}
                data-testid="calzone-tab"
              >
                Calzone ({calzoneData.length})
              </button>
            </div>
          </motion.div>

          {/* Items Grid */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid md:grid-cols-2 gap-3"
            >
              {currentItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: Math.min(index * 0.03, 0.2) }}
                  className="group relative bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 hover:border-red-400/30 hover:bg-white/10 transition-all duration-300"
                  data-testid={`${activeTab}-item-${item.id}`}
                >
                  {item.isPopular && (
                    <div className="absolute -top-2 right-4 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      <Star className="w-3 h-3 inline mr-1 fill-white" />
                      {language === 'de' ? 'Beliebt' : 'Popular'}
                    </div>
                  )}

                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h3 className="font-heading text-lg font-bold text-white group-hover:text-red-400 transition-colors">
                          {language === 'de' ? item.name : (item.nameEn || item.name)}
                        </h3>
                        {renderBadges(item)}
                      </div>
                      <p className="text-white/50 text-sm mt-1">
                        {language === 'de' ? item.description : (item.descriptionEn || item.description)}
                      </p>
                    </div>
                    <span className="font-bold text-amber-400 text-xl">€{item.price.toFixed(2)}</span>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default PastaCalzoneSection;
